import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAffiliateStats } from '../../../Redux/adminSlice';
import ContentLoader from '../../utils/ContentLoader';
import { FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import { TABS } from './constants';
import OverviewTab from './components/OverviewTab';
import PerformanceTab from './components/PerformanceTab';
import TopAffiliatesTab from './components/TopAffiliatesTab';
import RecentLinksTab from './components/RecentLinksTab';
import LocationInsightsSection from './components/LocationInsightsSection';

export default function AffiliateManagementPage() {
  const dispatch = useDispatch();
  const { stats, loading, error } = useSelector((state) => state.admin.affiliate);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    dispatch(fetchAffiliateStats());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchAffiliateStats());
  };

  const overview = stats?.overview ?? null;
  const timeSeries = stats?.timeSeries ?? null;
  const topAffiliates = stats?.topAffiliates ?? [];
  const recentLinks = stats?.recentLinks ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Affiliate Management</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Stats, performance, recent links, and location insights
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-60"
        >
          <FiRefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
          <FiAlertCircle className="h-5 w-5 shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {loading && <ContentLoader message="Loading affiliate data…" />}

      {!loading && (
        <>
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
            <nav className="flex border-b border-slate-100" aria-label="Tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-5 py-4 text-sm font-medium transition sm:flex-none ${
                    activeTab === tab.id
                      ? 'border-b-2 border-orange-500 bg-orange-50/50 text-orange-600'
                      : 'text-slate-500 hover:bg-slate-50/50 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'overview' && (
            <OverviewTab
              overview={overview}
              topAffiliates={topAffiliates}
              loading={loading}
            />
          )}
          {activeTab === 'performance' && <PerformanceTab timeSeries={timeSeries} />}
          {activeTab === 'affiliates' && <TopAffiliatesTab topAffiliates={topAffiliates} />}
          {activeTab === 'links' && <RecentLinksTab recentLinks={recentLinks} />}
          {activeTab === 'location' && (
            <LocationInsightsSection
              topAffiliates={topAffiliates}
              recentLinks={recentLinks}
            />
          )}
        </>
      )}
    </div>
  );
}
